"use client";

import { Mail, MapPin, Phone, Send, Loader, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
const contactMethods = [{
  icon: Phone,
  label: "TELEPHONE",
  primary: "(587) 888-4223",
  detail1: "Mon-Fri: 8am - 5pm",
  detail2: "Direct line to our project managers",
  detail3: "Serving Calgary and surrounding areas"
}, {
  icon: Mail,
  label: "EMAIL",
  primary: "info@metallineexteriors.ca",
  detail1: "Response within 24 hours",
  detail2: "For quotes, inquiries, and partnerships",
  detail3: "Secure digital project documentation"
}, {
  icon: MapPin,
  label: "OFFICE",
  primary: "330 5 Ave SW # 1800, Calgary, AB T2P 0L4",
  detail1: "Serving Airdrie, Okotoks, Cochrane",
  detail2: "By appointment only",
  detail3: "Professional site consultations available"
}];
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border border-border p-10 text-center bg-card">
        <p className="font-default text-2xl text-foreground mb-4">
          {successMessage}
        </p>
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-6">
          Your request has been received by our team
        </p>
        <Button onClick={resetForm} variant="outline" className="font-mono text-xs uppercase tracking-widest">
          Send Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" rules={{
      required: "Your name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Your Name" className="bg-transparent border-0 border-b-2 border-border px-0 h-12 font-default text-lg text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-primary w-full" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <FormField control={control} name="email" rules={{
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                Email Address (optional)
              </FormLabel>
              <FormControl>
                <Input type="email" inputMode="email" placeholder="Your Email" className="bg-transparent border-0 border-b-2 border-border px-0 h-12 font-default text-lg text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-primary w-full" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                Phone Number
              </FormLabel>
              <FormControl>
                <Input type="tel" inputMode="tel" placeholder="Your Phone Number" className="bg-transparent border-0 border-b-2 border-border px-0 h-12 font-default text-lg text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-primary w-full" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="postal_code" rules={{
        required: "Postal code is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                Postal Code
              </FormLabel>
              <FormControl>
                <Input type="text" autoComplete="postal-code" placeholder="Your Postal Code" className="bg-transparent border-0 border-b-2 border-border px-0 h-12 font-default text-lg text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-primary w-full" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="subject" rules={{
      required: "Please select a service"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Service Required
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="bg-transparent border-0 border-b-2 border-border px-0 h-12 font-default text-lg text-foreground focus:ring-0 focus-visible:ring-0 focus-visible:border-primary w-full data-[size=default]:h-12">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="Metal Roofing" className="font-default">Metal Roofing</SelectItem>
                  <SelectItem value="Siding & Cladding" className="font-default">Siding & Cladding</SelectItem>
                  <SelectItem value="Architectural Panels" className="font-default">Architectural Panels</SelectItem>
                  <SelectItem value="General Inquiry" className="font-default">General Inquiry</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Project Details (optional)
            </FormLabel>
            <FormControl>
              <Textarea rows={5} placeholder="Tell us about your exterior project..." className="bg-transparent border-0 border-b-2 border-border px-0 font-default text-lg text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-primary resize-none w-full" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="border border-destructive/40 p-4">
          <p className="font-mono text-xs text-destructive">{submitError}</p>
        </div>}

      <p className="text-xs text-muted-foreground">
        We use the details you submit to respond to your inquiry and prepare your quote. Read our{" "}
        <Link to="/PrivacyPolicyPage" className="text-primary underline underline-offset-4 hover:text-primary/80 transition-colors">
          Privacy Policy
        </Link>
        .
      </p>

      <div className="flex flex-col sm:flex-row gap-4">
        <Button type="submit" disabled={isSubmitting} className="flex-1 h-14 bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs uppercase tracking-[0.25em]">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Sending...
            </> : <>
              <Send className="mr-2 size-4" />
              Request Quote
            </>}
        </Button>
        <Button asChild variant="outline" className="flex-1 h-14 border-primary text-primary hover:bg-primary hover:text-primary-foreground font-mono text-xs uppercase tracking-[0.25em]">
          <a href="tel:+15878884223">
            <Phone className="mr-2 size-4" />
            (587) 888-4223
          </a>
        </Button>
      </div>
    </Form>;
}
export default function Contact() {
  const intro = useRevealOnScroll<HTMLDivElement>();
  const cards = useRevealOnScroll<HTMLDivElement>();
  const form = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="3472" id="lets-discuss-your-project" className="bg-background text-foreground ![padding:65px_48px_75px_48px]">
      <div className="max-w-7xl mx-auto">
        <div ref={intro.ref} className={`text-center max-w-4xl mx-auto transition-all duration-1000 ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-primary mb-10">
            Contact MetalLine
          </p>
          <h1 className="font-default text-5xl md:text-7xl lg:text-[96px] leading-[1.05] text-foreground mb-10">
            Ready to
            <br />
            <em className="text-primary not-italic font-default italic font-light">elevate your exterior?</em>
          </h1>
          <p className="font-default text-[17px] text-foreground/80 max-w-2xl mx-auto">
            Precision engineering meets architectural design. Reach out to our team for a consultation.
          </p>
        </div>

        <div ref={cards.ref} className={`mt-[100px] flex flex-col gap-6 md:grid md:grid-cols-3 md:gap-6 transition-all duration-1000 delay-150 ${cards.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {contactMethods.map((method, i) => {
          const Icon = method.icon;
          const cardClassName = "group relative bg-card border border-border hover:border-primary transition-all duration-500 p-10 md:p-14 flex flex-col min-h-[420px]";
          if (i === 0) {
            return <a key={i} data-index={i} href="tel:+15878884223" className={cardClassName}>
                <div className="mb-12">
                  <Icon className="size-8 text-primary transition-transform duration-500 group-hover:rotate-[5deg]" strokeWidth={1.5} />
                </div>

                <p className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground mb-6">
                  {method.label}
                </p>

                <p className="font-default text-foreground leading-snug mb-8 whitespace-nowrap text-2xl overflow-hidden text-ellipsis">{method.primary}</p>

                <div className="font-mono text-[13px] text-foreground/70 space-y-2 leading-relaxed">
                  <p>{method.detail1}</p>
                  <p>{method.detail2}</p>
                  <p>{method.detail3}</p>
                </div>

                <div className="mt-auto pt-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <span className="inline-flex items-center font-mono text-xs uppercase tracking-[0.25em] text-primary">
                    Connect
                    <ArrowRight className="ml-2 size-3" strokeWidth={1.5} />
                  </span>
                </div>
              </a>;
          }
          if (i === 1) {
            return <a key={i} data-index={i} href="mailto:info@metallineexteriors.ca" className={cardClassName}>
                <div className="mb-12">
                  <Icon className="size-8 text-primary transition-transform duration-500 group-hover:rotate-[5deg]" strokeWidth={1.5} />
                </div>

                <p className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground mb-6">
                  {method.label}
                </p>

                <p className="font-default text-foreground leading-snug mb-8 text-lg sm:text-2xl">{method.primary}</p>

                <div className="font-mono text-[13px] text-foreground/70 space-y-2 leading-relaxed">
                  <p>{method.detail1}</p>
                  <p>{method.detail2}</p>
                  <p>{method.detail3}</p>
                </div>

                <div className="mt-auto pt-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <span className="inline-flex items-center font-mono text-xs uppercase tracking-[0.25em] text-primary">
                    Connect
                    <ArrowRight className="ml-2 size-3" strokeWidth={1.5} />
                  </span>
                </div>
              </a>;
          }
          return <Link key={i} data-index={i} to="https://maps.google.com/?q=330+5+Ave+SW+%231800,+Calgary,+AB+T2P+0L4" newTab className={cardClassName}>
              <div className="mb-12">
                <Icon className="size-8 text-primary transition-transform duration-500 group-hover:rotate-[5deg]" strokeWidth={1.5} />
              </div>

              <p className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground mb-6">
                {method.label}
              </p>

              <p className="font-default text-foreground leading-snug mb-8 text-lg sm:text-2xl">{method.primary}</p>

              <div className="font-mono text-[13px] text-foreground/70 space-y-2 leading-relaxed">
                <p>{method.detail1}</p>
                <p>{method.detail2}</p>
                <p>{method.detail3}</p>
              </div>

              <div className="mt-auto pt-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <span className="inline-flex items-center font-mono text-xs uppercase tracking-[0.25em] text-primary">
                  Connect
                  <ArrowRight className="ml-2 size-3" strokeWidth={1.5} />
                </span>
              </div>
            </Link>;
        })}
        </div>

        <div className="flex items-center justify-center ![margin:95px_0px_0px_0px]">
          <div className="flex-1 h-px bg-border" />
          
          <div className="flex-1 h-px bg-border" />
        </div>

        <div ref={form.ref} className={`mt-24 flex flex-col lg:flex-row gap-12 transition-all duration-1000 ${form.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="bg-card border border-border p-10 md:p-14 flex flex-col lg:w-[380px] shrink-0 justify-between">
            <div>
              <div className="mb-12">
                <MapPin className="size-8 text-primary" strokeWidth={1.5} />
              </div>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground mb-6">
                HEADQUARTERS
              </p>
              <p className="font-default text-2xl text-foreground leading-snug mb-8 break-words">330 5 Ave SW&nbsp; # 1800, Calgary, AB T2P 0L4</p>
              <div className="font-mono text-[13px] text-foreground/70 space-y-2 leading-relaxed">
                
                <p>Visits by appointment</p>
                <p>Client consultations & planning</p>
              </div>
            </div>
          </div>

          <div className="flex-1">
            <div className="text-center lg:text-left mb-16">
              <p className="font-mono text-xs uppercase tracking-[0.35em] text-primary mb-6">
                Project Inquiry
              </p>
              <h2 className="font-default text-4xl md:text-5xl text-foreground">
                Start your <em className="text-primary not-italic italic font-light">renovation journey</em>
              </h2>
            </div>

            <FormProvider formKey="metalline_contact_inquiry_v2" formId="metalline_contact_inquiry_v2" sectionName="lets-discuss-your-project" defaultValues={{
            name: "",
            email: "",
            phone: "",
            postal_code: "",
            subject: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: false,
              pattern: "email"
            },
            phone: {
              required: true
            },
            postal_code: {
              required: true
            },
            subject: {
              required: true
            },
            message: {
              required: false
            }
          }} submitText="Request Quote" successMessage="Thank you! Your request has been submitted successfully. We will contact you shortly." errorMessage="We were unable to process your request. Please try again.">
              <InquiryForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}